"use client";

import { useState } from "react";
import { ChevronDown, Mail, Sparkles } from "lucide-react";

import type {
  MissingInfoItem,
  MissingInfoKey,
} from "@/lib/projects/missing-info";

interface Props {
  items: readonly MissingInfoItem[];
  onRequest: (key: MissingInfoKey) => void;
}

/**
 * Accordion of "pendiente" info categories. Each expands to a short
 * description + "Pídelo" button that asks the parent wrapper to seed
 * the inquiry form with the corresponding pre-fill message and scroll
 * to it.
 */
export function MissingInfoSection({ items, onRequest }: Props) {
  const [openKey, setOpenKey] = useState<MissingInfoKey | null>(null);

  return (
    <section
      aria-label="Información complementaria"
      className="my-12 max-w-3xl rounded-3xl bg-cream-100 p-7 ring-1 ring-ink-200 sm:p-9"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="mt-1 h-6 w-6 shrink-0 text-coral" aria-hidden />
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-coral">
            Información complementaria
          </p>
          <h2 className="mt-2 text-2xl font-bold text-ink">
            ¿Necesitas más datos del desarrollador?
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-ink-700">
            Algunos datos los manejamos por solicitud — te llegan en{" "}
            <strong className="text-ink">24-48 horas</strong> directamente del
            desarrollador, junto a una llamada de seguimiento si la pides. Marca
            lo que te interesa.
          </p>
        </div>
      </div>

      <ul className="mt-6 divide-y divide-ink-200 overflow-hidden rounded-2xl bg-white ring-1 ring-ink-200">
        {items.map((item) => {
          const open = openKey === item.key;
          return (
            <li key={item.key}>
              <button
                type="button"
                onClick={() => setOpenKey(open ? null : item.key)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition hover:bg-cream-50"
              >
                <span className="font-medium text-ink">{item.title}</span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-ink-500 transition ${
                    open ? "rotate-180" : ""
                  }`}
                  aria-hidden
                />
              </button>
              {open && (
                <div className="border-t border-ink-100 bg-cream-50/50 px-5 py-4">
                  <p className="text-sm leading-relaxed text-ink-700">
                    {item.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => onRequest(item.key)}
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-coral px-4 py-2 text-sm font-semibold text-cream-50 shadow-sm transition hover:bg-coral-600"
                  >
                    <Mail className="h-4 w-4" aria-hidden />
                    Pídelo
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
